/**
 * Ryan O'Dowd
 * 2021-05-28
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  AppState,
  Platform,
  TouchableOpacity,
  View,
} from 'react-native';
import {
  ImageLibraryOptions,
  launchCamera,
  launchImageLibrary,
} from 'react-native-image-picker';
import React, {
  useEffect,
  useState,
} from 'react';
import ActivityIndicator from '@components/common/ActivityIndicator';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import {
  IconButton,
} from 'react-native-paper';
import Image from './Image';
import Text from '@components/common/Text';
import Viewer from './Viewer';
import {
  add as dateFnsAdd,
} from 'date-fns';
import fb from '@utils/firebase';
import {
  fetchProfilePictureUrl,
} from '@utils/store/actions';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const PERSON_PLACEHOLDER_IMAGE = require('@assets/img/avatar.png');

const IMAGE_OPTIONS: ImageLibraryOptions = {
  mediaType: 'photo',
};

const Photo = (props: PhotoProps) => {
  const i18n = hooks.useI18n();
  const styles = hooks.useStyles(getStyles, {badgeSize: props.badgeSize});
  const dispatch = hooks.useTypedDispatch();

  const photoUrl = hooks.useGetPhotoUrl(props.username);
  const isLoggedInUser = hooks.useTypedSelector((state) => state.oakUser.username === props.username);

  const [_isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [_isUploadingImage, setIsUploadingImage] = useState(false);
  const [_isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (['inactive', 'background'].includes(nextAppState)) {
        setIsImageViewerOpen(false);
      }
    });

    return () => subscription?.remove();
  }, []);

  function _imagePickerCallback(response, pickerType) {
    if (!response.didCancel && response.error) {
      fb.log(fb.crashlytics, `image-picker (${pickerType}) error: \`${response.error}\``);
      fb.recordError(fb.crashlytics, response.error);
    } else if (!response.didCancel) {
      const image = response.assets[0];
      utilities.uploadPhoto(
        image.uri,
        globals.profilePictureSize,
        `${globals.apiUrl}/user/profile_picture`,
        () => setIsUploadingImage(true),
        () => {
          dispatch(fetchProfilePictureUrl(props.username));
          setIsUploadingImage(false);
        },
      );
    }
  }

  const launchImageLibraryWrapper = () => {
    return launchImageLibrary(IMAGE_OPTIONS, (response) => _imagePickerCallback(response, 'library'));
  };

  function getTimestamp(dateString) { // @TODO: i would prefer that timestamps are always the same format... (right now can be `Mon, 30 Dec 2024 01:28:53 GMT` or `2024-12-30 01:28:53` depending on if this is a photo in widget or profile)
    // from chatgpt
    const date = new Date(dateString);
    return date.getTime();
  }

  const lastActiveTimestamp = getTimestamp(props.lastActive ? (props.lastActive.includes('GMT') ? props.lastActive : `${props.lastActive}Z`) : 0);
  const adjustedTime = dateFnsAdd(new Date(lastActiveTimestamp), {minutes: 10}).getTime(); // @TODO: rename

  return (
    <React.Fragment>
      <TouchableOpacity
        style={[styles.container, props.style]}
        disabled={props.isPressDisabled}
        onPress={() => {
          dispatch(fetchProfilePictureUrl(props.username));
          if (props.onPress) {
            props.onPress();
          } else {
            setIsImageViewerOpen(true);
          }
        }}
        onLongPress={() => {
          dispatch(fetchProfilePictureUrl(props.username));
          if (props.onLongPress) {
            props.onLongPress();
          } else if (props.onPress) {
            props.onPress();
          } else {
            setIsImageViewerOpen(true);
          }
        }}
      >
        <Image
          style={[styles.image, props.imageStyle]}
          resizeMode='cover'
          source={!photoUrl ? PERSON_PLACEHOLDER_IMAGE : {
            uri: photoUrl,
            priority: 'normal', // @TODO: make sure this is the same as FastImage.priority.normal,
          }}
          onError={() => {
            // probably still in redux but not in the FastImage cache, so refetch here
            dispatch(fetchProfilePictureUrl(props.username));
          }}
          username={props.username}
        />
        {_isUploadingImage && <ActivityIndicator style={styles.imageLoadingIndicator} animating={true} size={30} />}
      </TouchableOpacity>
      <Viewer
        image={photoUrl ? {uri: photoUrl} : PERSON_PLACEHOLDER_IMAGE}
        visible={_isImageViewerOpen}
        onRequestClose={() => setIsImageViewerOpen(false)}
        footerComponent={
          <View style={styles.viewerFooter}>
            <View>
              {!props.readOnly && <View style={styles.foo} />}{/* @TODO: remove when delete button is added back */}
              {/* @TODO:
              {!props.readOnly &&
                <IconButton
                  icon='delete'
                  iconColor={Platform.OS === 'web' ? styles.colors.dialogText : styles.colors.white}
                  disabled={!_photoUrl}
                  size={30}
                  onPress={() => {
                    // @TODO: delete image from s3
                    setIsImageViewerOpen(false);
                  }}
                />
              }
              */}
            </View>
            <View style={styles.footerTextWrapper}>
              <Text style={styles.displayNameText}>{props.displayName}</Text>{/* @TODO: this isn't centered when readonly is true */}
              <Text style={styles.usernameText}>{props.username}</Text>
            </View>
            <View>
              {!props.readOnly &&
                <IconButton
                  icon='upload'
                  iconColor={styles.colors.dialogText}
                  size={30}
                  onPress={() => {
                    if (Platform.OS === 'web') {
                      launchImageLibraryWrapper();
                    } else {
                      setIsUploadDialogOpen(true);
                    }
                    setIsImageViewerOpen(false);
                  }}
                />
              }
            </View>
          </View>
        }
      />
      {(!isLoggedInUser && (adjustedTime >= Date.now())) && <View style={styles.recentlyActiveBadge} />}
      {_isUploadDialogOpen &&
        <Dialog
          visible={_isUploadDialogOpen}
          onDismiss={() => setIsUploadingImage(false)}
        >
          <Dialog.Title>{i18n('uploadMethod')}</Dialog.Title>
          <Dialog.Content>
            <Text>{i18n('whatKindOfPhoto')}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='text'
              onPress={() => {
                setIsUploadDialogOpen(false);
              }}
            >
              {i18n('cancel')}
            </Button>
            <Button
              mode='contained'
              onPress={() => {
                launchImageLibraryWrapper();
                setIsUploadDialogOpen(false);
              }}
            >
              {i18n('selectExistingPhoto')}
            </Button>
            <Button
              mode='contained'
              onPress={() => {
                launchCamera(IMAGE_OPTIONS, (response) => _imagePickerCallback(response, 'camera'));
                setIsUploadDialogOpen(false);
              }}
            >
              {i18n('captureNewPhoto')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
    </React.Fragment>
  );
};

class PhotoProps {
  displayName: string;
  username: string;
  badgeSize: number; // @TODO: don't like passing this in (also make sure there's `globalScale *`)

  lastActive?: string | 0;
  description?: string;
  imageStyle?: any;
  isPressDisabled?: boolean;
  onLongPress?: () => void; // @TODO: is this used?
  onPress?: () => void;
  readOnly?: boolean;
  style?: any;
};

export default Photo;
